import { Env } from "@/env";
import { FollowingLogo } from "@/views/layout/header";
import { useUser } from "@features/user/use-user";
import { Heading, Text } from "@radix-ui/themes";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import AppleSignin from "react-apple-signin-auth";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface LoginFormProps {
  onLoginStart?: () => void;
  onLoginEnd?: () => void;
  isFromUpload?: boolean;
  compact?: boolean; // For modal view
}

export const LoginForm = ({
  onLoginStart,
  onLoginEnd,
  isFromUpload = false,
  compact = false,
}: LoginFormProps) => {
  const { login } = useUser();
  const { t } = useTranslation();

  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source") || "";

  const redirectUrl = window.location.origin;

  const handleGoogleLogin = async (credential: string) => {
    onLoginStart?.();
    try {
      await login("google", credential, { utm_source: utmSource });
    } catch (e) {
      toast.error(t("login.failed_to_login"));
    } finally {
      onLoginEnd?.();
    }
  };

  const handleAppleLogin = async (response: any) => {
    onLoginStart?.();
    try {
      await login("apple", response.authorization.code, {
        redirect_url: redirectUrl,
        name: [response.user?.name?.firstName, response.user?.name?.lastName]
          .filter(Boolean)
          .join(" "),
      });
    } catch (e) {
      toast.error(t("login.failed_to_login"));
    } finally {
      onLoginEnd?.();
    }
  };

  const buttonWidth = compact ? 280 : 320;

  // Style for Google button to remove borders and ensure consistent sizing
  const googleButtonStyle = `
    .google-button-container iframe {
      border: none !important;
      box-shadow: none !important;
      height: 48px !important;
    }
    .auth-button-wrapper {
      display: flex;
      justify-content: center;
      width: 100%;
    }
    .apple-auth-button {
      border-radius: 24px !important;
      overflow: hidden;
    }
  `;

  return (
    <div className="flex flex-col items-center space-y-8 text-center">
      {/* Logo */}
      <div className="flex items-center space-x-2">
        <FollowingLogo />
        <Text size="5" className="font-bold cursor-pointer">
          Photographe<Text size="1">.ai</Text>
        </Text>
      </div>

      {/* Title */}
      <Heading size={compact ? "6" : "7"}>
        {isFromUpload ? t("login.login_to_continue") : t("login.welcome")}
      </Heading>

      <div
        className="flex flex-col items-center text-center space-y-6 w-full"
        style={{ colorScheme: "none" }}
      >
        {/* Subtitle */}
        <Text size="2" className="text-gray-600 dark:text-gray-300">
          {isFromUpload
            ? t("login.signin_to_continue_upload")
            : t("login.signin_to_continue")}
        </Text>

        {/* Inject style to remove Google button borders */}
        <style dangerouslySetInnerHTML={{ __html: googleButtonStyle }} />

        {/* Google Login */}
        <div className="w-full">
          <GoogleOAuthProvider clientId={Env.googleoauth}>
            <div className="auth-button-wrapper google-button-container">
              <GoogleLogin
                width={buttonWidth}
                size="large"
                onSuccess={(credentialResponse) =>
                  handleGoogleLogin(credentialResponse.credential || "")
                }
                onError={() => {
                  toast.error(t("login.failed_to_login"));
                }}
                text="continue_with"
                shape="pill"
                locale="en"
              />
            </div>
          </GoogleOAuthProvider>
        </div>

        {/* Apple Login */}
        <div className="w-full">
          <div className="auth-button-wrapper">
            <AppleSignin
              uiType="dark"
              className={`w-[${buttonWidth}px] h-[44px] w-full mx-4 !text-[14px] apple-auth-button`}
              authOptions={{
                clientId: "com.photographeai.signin",
                scope: "email name",
                redirectURI: redirectUrl,
                state: "state",
                nonce: "nonce",
                usePopup: true,
              }}
              onSuccess={handleAppleLogin}
              onError={(error: any) => {
                console.error("Apple error", error);
                toast.error(t("login.failed_to_login"));
              }}
            />
          </div>
        </div>

        {/* Login to app link (for React Native WebView) */}
        {!compact && (window as any).ReactNativeWebView && (
          <div>
            <a
              href="/?login-to-app=1&redirect=1"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              {t("login.login_to_app")}
            </a>
          </div>
        )}

        {/* Terms */}
        <a
          href="https://photographe.ai/legal/tos/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          <Text size={compact ? "1" : "2"}>{t("login.accept_tos")}</Text>
        </a>
      </div>
    </div>
  );
};
